// src/pages/staff/MySettingsPage.jsx
import React, { useState, useEffect } from "react";
import Sidebar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import { updateStaff, getOneStaff } from "../../api/staff-calls-api";
import Select from "react-select";
import countries from "../../utils/countryList";
import {
    User, Briefcase, CreditCard, GraduationCap, Users, MapPin, Phone, Mail,
    Calendar, Lock, Save, X, CheckCircle, AlertCircle, Info
} from "lucide-react";
import axios from "axios"; // ✅ for resignation API call

const countryOptions = countries.map((c) => ({ label: `${c.flag} ${c.name}`, value: c.name }));

const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" }
];

const maritalStatusOptions = [
    { value: "Single", label: "Single" },
    { value: "Married", label: "Married" },
    { value: "Divorced", label: "Divorced" },
    { value: "Widowed", label: "Widowed" }
];

const Section = ({ title, icon, children, isOpen, onToggle }) => {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden transition-all duration-300">
            <button
                type="button"
                onClick={onToggle}
                className="w-full text-left px-6 py-4 bg-gradient-to-r from-orange-50 to-white font-semibold text-gray-800 flex justify-between items-center hover:from-orange-100 transition-all duration-200"
            >
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                        {icon}
                    </div>
                    <span className="text-lg">{title}</span>
                </div>
                <div className="transform transition-transform duration-300">
                    {isOpen ? (
                        <div className="p-1 bg-gray-100 rounded-full">
                            <X className="w-4 h-4 text-gray-600" />
                        </div>
                    ) : (
                        <div className="p-1 bg-gray-100 rounded-full">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                        </div>
                    )}
                </div>
            </button>
            {isOpen && <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>}
        </div>
    );
};

export default function MySettingsPage() {
    const user = JSON.parse(localStorage.getItem("user"));
    const staffId = user?.id;
    const token = localStorage.getItem("token"); // ✅ token for resignation
    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/";
    };

    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [openSections, setOpenSections] = useState({
        personal: true,
        employment: true,
        banking: false,
        education: false,
        nextOfKin: false
    });

    // ✅ Resignation form state removed

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const formatDate = (dateString) => (dateString ? new Date(dateString).toISOString().split("T")[0] : "");

    // Fetch staff data asynchronously
    useEffect(() => {
        async function fetchStaff() {
            try {
                const res = await getOneStaff(staffId);
                const data = res.data || res;

                const flattenedData = {
                    ...data,
                    dateOfBirth: formatDate(data.dateOfBirth),
                    villageOrBlock: data.residence?.villageOrBlock || "",
                    payam: data.residence?.payam || "",
                    county: data.residence?.county || "",
                    state: data.residence?.state || "",
                    jobTitle: data.jobTitle || "",
                    department: data.department || "",
                    dateOfEmployment: formatDate(data.dateOfEmployment),
                    employmentType: data.employmentType || "",
                    contractDuration: data.contractDuration || "",
                    salaryRange: data.salaryRange || "",
                    bankName: data.bankingDetails?.[0]?.bankName || "",
                    bankBranch: data.bankingDetails?.[0]?.bankBranch || "",
                    accountName: data.bankingDetails?.[0]?.accountName || "",
                    accountNumber: data.bankingDetails?.[0]?.accountNumber || "",
                    currency: data.bankingDetails?.[0]?.currency || "",
                    swiftCode: data.bankingDetails?.[0]?.swiftCode || "",
                    institution: data.qualifications?.[0]?.institution || "",
                    award: data.qualifications?.[0]?.award || "",
                    yearFrom: data.qualifications?.[0]?.yearFrom || "",
                    yearTo: data.qualifications?.[0]?.yearTo || "",
                    keyExpertise: data.workHistory?.keyExpertise || "",
                    recentEmployer: data.workHistory?.recentEmployer || "",
                    workrole: data.workHistory?.workrole || "",
                    periodOfService: data.workHistory?.periodOfService || "",
                    recentGrossAnnualPaymentRange: data.workHistory?.recentGrossAnnualPaymentRange || "",
                    nokName: data.nextOfKin?.name || "",
                    nokSex: data.nextOfKin?.sex || "",
                    nokNationality: data.nextOfKin?.nationality || "",
                    nokRelationship: data.nextOfKin?.relationship || "",
                    nokVillageOrBlock: data.nextOfKin?.residence?.villageOrBlock || "",
                    nokPayam: data.nextOfKin?.residence?.payam || "",
                    nokCounty: data.nextOfKin?.residence?.county || "",
                    nokState: data.nextOfKin?.residence?.state || "",
                    nokTel: data.nextOfKin?.tel || "",
                    nokMobile: data.nextOfKin?.mobile || "",
                    nokEmail: data.nextOfKin?.email || "",
                };
                setFormData(flattenedData);
            } catch (err) {
                setMessage({ type: "error", text: "Failed to load profile" });
            } finally {
                setLoading(false);
            }
        }
        fetchStaff();
    }, [staffId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);

        const updatedStaff = {
            ...formData,
            residence: {
                villageOrBlock: formData.villageOrBlock,
                payam: formData.payam,
                county: formData.county,
                state: formData.state,
            },
            nextOfKin: {
                name: formData.nokName,
                sex: formData.nokSex,
                nationality: formData.nokNationality,
                relationship: formData.nokRelationship,
                residence: {
                    villageOrBlock: formData.nokVillageOrBlock,
                    payam: formData.nokPayam,
                    county: formData.nokCounty,
                    state: formData.nokState,
                },
                tel: formData.nokTel,
                mobile: formData.nokMobile,
                email: formData.nokEmail,
            },
            bankingDetails: [{
                bankName: formData.bankName,
                bankBranch: formData.bankBranch,
                accountName: formData.accountName,
                accountNumber: formData.accountNumber,
                currency: formData.currency,
                swiftCode: formData.swiftCode,
            }],
            qualifications: [{
                yearFrom: formData.yearFrom,
                yearTo: formData.yearTo,
                institution: formData.institution,
                award: formData.award,
            }],
            workHistory: {
                keyExpertise: formData.keyExpertise,
                recentEmployer: formData.recentEmployer,
                workrole: formData.workrole,
                periodOfService: formData.periodOfService,
                recentGrossAnnualPaymentRange: formData.recentGrossAnnualPaymentRange,
            },
        };

        try {
            await updateStaff(staffId, updatedStaff);
            setMessage({ type: "success", text: "Profile updated successfully" });
        } catch (err) {
            setMessage({ type: "error", text: "Failed to update profile" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans">
            <Sidebar onLogout={handleLogout} />
            <main className="flex-1 p-6 overflow-y-auto md:ml-64">
                <TopBar />

                <div className="mt-20 max-w-5xl mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">My Settings</h1>
                            <p className="text-gray-600 mt-2">Manage your personal information and preferences</p>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">My Settings</h1>
                            <p className="text-gray-600 mt-2">Manage your personal information and preferences</p>
                        </div>
                    </div>

                    {/* Message */}
                    {message && (
                        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                            {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                            <span>{message.text}</span>
                        </div>
                    )}

                    {/* Form container */}
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6 relative">
                        {loading && (
                            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-xl">
                                <div className="flex flex-col items-center">
                                    <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                    <div className="mt-4 text-gray-700 font-medium">Loading profile...</div>
                                </div>
                            </div>
                        )}

                        {/* Personal Information Section */}
                        <Section
                            title="Personal Information"
                            icon={<User className="w-5 h-5" />}
                            isOpen={openSections.personal}
                            onToggle={() => toggleSection('personal')}
                        >
                            {renderField("First Name", "firstName", "text", null, true)}
                            {renderField("Last Name", "lastName", "text", null, true)}
                            {renderField("Email", "email", "email", null, true)}
                            {renderSelectField("Gender", "gender", genderOptions, true)}
                            {renderField("Date of Birth", "dateOfBirth", "date", null, true)}
                            {renderField("Role", "role", "text", null, true)}
                            {renderSelectField("Marital Status", "maritalStatus", maritalStatusOptions)}
                            {renderCountryField("Nationality", "nationality")}
                            {renderField("National ID or Passport", "nationalIdOrPassport")}
                            {renderField("Village or Block", "villageOrBlock")}
                            {renderField("Payam", "payam")}
                            {renderField("County", "county")}
                            {renderField("State", "state")}
                            {renderField("Telephone", "tel")}
                            {renderField("Mobile", "mobile")}
                        </Section>

                        {/* Employment Details Section */}
                        <Section
                            title="Employment Details"
                            icon={<Briefcase className="w-5 h-5" />}
                            isOpen={openSections.employment}
                            onToggle={() => toggleSection('employment')}
                        >
                            {renderField("Job Title", "jobTitle", "text", null, true)}
                            {renderField("Department", "department", "text", null, true)}
                            {renderField("Date of Employment", "dateOfEmployment", "date", null, true)}
                            {renderField("Employment Type", "employmentType", "text", null, true)}
                            {renderField("Contract Duration", "contractDuration", "text", null, true)}
                            {renderField("Salary Range", "salaryRange", "text", null, true)}
                        </Section>

                        {/* Banking Details Section */}
                        <Section
                            title="Banking Details"
                            icon={<CreditCard className="w-5 h-5" />}
                            isOpen={openSections.banking}
                            onToggle={() => toggleSection('banking')}
                        >
                            {renderField("Bank Name", "bankName")}
                            {renderField("Bank Branch", "bankBranch")}
                            {renderField("Account Name", "accountName")}
                            {renderField("Account Number", "accountNumber")}
                            {renderField("Currency", "currency")}
                            {renderField("SWIFT Code", "swiftCode")}
                        </Section>

                        {/* Education & Work History Section */}
                        <Section
                            title="Education & Work History"
                            icon={<GraduationCap className="w-5 h-5" />}
                            isOpen={openSections.education}
                            onToggle={() => toggleSection('education')}
                        >
                            {renderField("Institution", "institution")}
                            {renderField("Award", "award")}
                            {renderField("Year From", "yearFrom")}
                            {renderField("Year To", "yearTo")}
                            {renderField("Key Expertise", "keyExpertise")}
                            {renderField("Recent Employer", "recentEmployer")}
                            {renderField("Role", "workrole")}
                            {renderField("Period of Service", "periodOfService")}
                            {renderField("Recent Gross Annual Payment Range", "recentGrossAnnualPaymentRange")}
                        </Section>

                        {/* Next of Kin Section */}
                        <Section
                            title="Next of Kin Information"
                            icon={<Users className="w-5 h-5" />}
                            isOpen={openSections.nextOfKin}
                            onToggle={() => toggleSection('nextOfKin')}
                        >
                            {renderField("Next of Kin Name", "nokName")}
                            {renderSelectField("Next of Kin Sex", "nokSex", genderOptions)}
                            {renderCountryField("Next of Kin Nationality", "nokNationality")}
                            {renderField("Next of Kin Relationship", "nokRelationship")}
                            {renderField("Next of Kin Village or Block", "nokVillageOrBlock")}
                            {renderField("Next of Kin Payam", "nokPayam")}
                            {renderField("Next of Kin County", "nokCounty")}
                            {renderField("Next of Kin State", "nokState")}
                            {renderField("Next of Kin Telephone", "nokTel")}
                            {renderField("Next of Kin Mobile", "nokMobile")}
                            {renderField("Next of Kin Email", "nokEmail", "email")}
                        </Section>

                        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-6 py-3 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 transition-colors duration-200 flex items-center space-x-2 disabled:opacity-70"
                            >
                                {saving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Save Changes</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* ResignationForm removed */}
                </div>
            </main>
        </div>
    );

    function renderField(label, name, type = "text", options, readOnly = false) {
        const iconMap = {
            email: <Mail className="w-4 h-4 text-gray-400" />,
            tel: <Phone className="w-4 h-4 text-gray-400" />,
            mobile: <Phone className="w-4 h-4 text-gray-400" />,
            date: <Calendar className="w-4 h-4 text-gray-400" />
        };

        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {readOnly && <span className="text-xs text-gray-500 ml-1">(Read-only)</span>}
                </label>
                <div className="relative">
                    {iconMap[type] && (
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            {iconMap[type]}
                        </div>
                    )}
                    {options ? (
                        <select
                            name={name}
                            value={formData[name] || ""}
                            onChange={handleChange}
                            className={`w-full border ${readOnly ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-300'} rounded-lg px-3 py-2.5 ${iconMap[type] ? 'pl-10' : ''} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200`}
                            disabled={readOnly}
                        >
                            <option value="">-- Select {label} --</option>
                            {options.map((opt, i) => (
                                <option key={i} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            name={name}
                            type={type}
                            value={formData[name] || ""}
                            onChange={handleChange}
                            className={`w-full border ${readOnly ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-300'} rounded-lg px-3 py-2.5 ${iconMap[type] ? 'pl-10' : ''} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200`}
                            readOnly={readOnly}
                        />
                    )}
                </div>
            </div>
        );
    }

    function renderSelectField(label, name, options, readOnly = false) {
        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {readOnly && <span className="text-xs text-gray-500 ml-1">(Read-only)</span>}
                </label>
                <Select
                    options={options}
                    value={options.find(opt => opt.value === formData[name]) || null}
                    onChange={(sel) => setFormData({ ...formData, [name]: sel?.value || "" })}
                    isDisabled={readOnly}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable
                    styles={{
                        control: (base, state) => ({
                            ...base,
                            borderRadius: '0.5rem',
                            borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                            '&:hover': {
                                borderColor: '#F97316'
                            },
                            backgroundColor: readOnly ? '#F3F4F6' : 'white'
                        }),
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                            color: state.isSelected ? 'white' : '#1F2937',
                            '&:hover': {
                                backgroundColor: '#FFEDD5',
                                color: '#1F2937'
                            }
                        }),
                    }}
                />
            </div>
        );
    }

    function renderCountryField(label, name, readOnly = false) {
        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {readOnly && <span className="text-xs text-gray-500 ml-1">(Read-only)</span>}
                </label>
                <Select
                    options={countryOptions}
                    value={countryOptions.find(opt => opt.value === formData[name]) || null}
                    onChange={(sel) => setFormData({ ...formData, [name]: sel?.value || "" })}
                    isDisabled={readOnly}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable
                    styles={{
                        control: (base, state) => ({
                            ...base,
                            borderRadius: '0.5rem',
                            borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                            boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                            '&:hover': {
                                borderColor: '#F97316'
                            },
                            backgroundColor: readOnly ? '#F3F4F6' : 'white'
                        }),
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                            color: state.isSelected ? 'white' : '#1F2937',
                            '&:hover': {
                                backgroundColor: '#FFEDD5',
                                color: '#1F2937'
                            }
                        }),
                    }}
                />
            </div>
        );
    }
}
